import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  deletePersonalLoan,
  getPersonalLoan,
  undoPersonalLoanPayment,
  writeOffPersonalLoan,
  type PersonalLoanPayment,
} from '@/api/personalLoans'
import { ConfirmDialog } from '@/components/personalLoans/ConfirmDialog'
import { EditTermsModal } from '@/components/personalLoans/EditTermsModal'
import { RecordPaymentModal } from '@/components/personalLoans/RecordPaymentModal'
import { errorDetail, invalidateAfterLoanWrite } from '@/components/personalLoans/shared'
import { LoanStatusBadges } from '@/components/personalLoans/LoanStatusBadges'
import { Button } from '@/components/ui/button'
import { LoanActionsBar } from '@/components/loans/LoanActionsBar'
import { HERO_SHELL, HERO_TONE, useHeroFormat } from '@/components/loans/loanHero'
import { DatePicker } from '@/components/DatePicker'
import { useFormatCurrency, useListAmountFormat } from '@/hooks/useFormatCurrency'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { formatDate, formatShortDate } from '@/lib/formatDate'
import { formatInterest } from '@/lib/personalLoans'
import { fromPaise, toPaise } from '@/lib/splitMath'
import { today } from '@/lib/transactionDateRangeFilter'
import { ROUTES } from '@/routes/routes'

function HeroFigure({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-[var(--fl-hero-fg-2)] md:text-[13px]">{label}</dt>
      <dd className="whitespace-nowrap text-[13px] font-semibold tabular-nums md:mt-0.5 md:font-serif md:text-2xl">
        {value}
      </dd>
    </div>
  )
}

export default function PersonalLoanDetailPage() {
  const { accountId = '' } = useParams<{ accountId: string }>()
  const { format } = useFormatCurrency()
  const heroFormat = useHeroFormat()
  const listFormat = useListAmountFormat()
  const isReadOnly = useIsReadOnly()
  const queryClient = useQueryClient()
  const [paying, setPaying] = useState(false)
  const [editing, setEditing] = useState(false)
  const [writingOff, setWritingOff] = useState(false)
  const [undoing, setUndoing] = useState<PersonalLoanPayment | null>(null)
  const [writeOffDate, setWriteOffDate] = useState(today())
  const [deleting, setDeleting] = useState(false)
  const navigate = useNavigate()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['personal-loans', accountId],
    queryFn: () => getPersonalLoan(accountId),
    enabled: !!accountId,
  })

  const writeOff = useMutation({
    mutationFn: () => writeOffPersonalLoan(accountId, writeOffDate),
    onSuccess: () => {
      invalidateAfterLoanWrite(queryClient)
      setWritingOff(false)
    },
  })
  const remove = useMutation({
    mutationFn: () => deletePersonalLoan(accountId),
    onSuccess: () => {
      invalidateAfterLoanWrite(queryClient)
      navigate(ROUTES.APP.LOANS)
    },
  })
  const undo = useMutation({
    mutationFn: (p: PersonalLoanPayment) => undoPersonalLoanPayment(accountId, p.id),
    onSuccess: () => {
      invalidateAfterLoanWrite(queryClient)
      setUndoing(null)
    },
  })

  if (isLoading) return <p className="p-6 text-sm text-[var(--fl-text-2)]">Loading…</p>
  if (isError || !data)
    return <p className="p-6 text-sm text-[var(--fl-error)]">Failed to load this loan.</p>

  const { loan, payments, openingAccountName } = data
  const lent = loan.direction === 'LENT'
  const person = loan.contact.name
  const principal = fromPaise(
    toPaise(loan.outstanding) + toPaise(loan.principalRepaid) + toPaise(loan.writtenOff)
  )

  function describe(p: PersonalLoanPayment): string {
    if (p.kind === 'WRITE_OFF') return `Written off ${listFormat(p.principal)}`
    const parts = [
      p.principal > 0 ? `${listFormat(p.principal)} principal` : null,
      p.interest > 0 ? `${listFormat(p.interest)} interest` : null,
    ].filter(Boolean)
    return `${lent ? `${person} paid` : `You paid ${person}`} ${parts.join(' + ')}`
  }

  return (
    <div
      className={`flex w-full flex-col gap-3 p-4 md:gap-5 md:p-10 ${isReadOnly ? '' : 'pb-28 sm:pb-10'}`}
    >
      <Link
        to={ROUTES.APP.LOANS}
        className="self-start text-sm text-[var(--fl-text-2)] hover:text-[var(--fl-heading)]"
      >
        ← Loans
      </Link>
      <section aria-label="Summary" className={`${HERO_SHELL} flex flex-col gap-2.5 md:gap-5`}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="break-words font-serif text-[17px] font-semibold leading-tight md:text-[26px]">
              {loan.name}
            </h1>
            <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[var(--fl-hero-fg-2)] md:text-[13px]">
              <Link to={ROUTES.APP.PERSON_DETAIL(loan.contact.id)} className="hover:underline">
                {lent ? `Lent to ${person}` : `Borrowed from ${person}`}
              </Link>
              <span aria-hidden="true">·</span>
              <span>{formatInterest(loan.interestBasis, loan.interestRate)}</span>
              {loan.dueDate && (
                <>
                  <span aria-hidden="true">·</span>
                  <span>Due {formatDate(loan.dueDate)}</span>
                </>
              )}
              <LoanStatusBadges loan={loan} />
            </p>
            {loan.notes && (
              <p className="mt-1 whitespace-pre-line text-xs text-[var(--fl-hero-fg-2)] md:text-[13px]">
                Reason: {loan.notes}
              </p>
            )}
          </div>
          {!isReadOnly && (
            <LoanActionsBar
              primary={
                loan.closed
                  ? undefined
                  : { label: 'Record payment', onClick: () => setPaying(true) }
              }
              items={[
                ...(lent && !loan.closed && loan.outstanding > 0
                  ? [
                      {
                        label: 'Write off',
                        hint: 'Record what won’t come back as a loss',
                        onSelect: () => {
                          writeOff.reset()
                          setWriteOffDate(today())
                          setWritingOff(true)
                        },
                      },
                    ]
                  : []),
                {
                  label: 'Edit terms',
                  hint: 'Interest, due date, reason',
                  onSelect: () => setEditing(true),
                },
                ...(payments.length === 0
                  ? [
                      {
                        label: 'Delete loan',
                        danger: true,
                        onSelect: () => {
                          remove.reset()
                          setDeleting(true)
                        },
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </div>

        <div className="flex flex-col gap-2 md:flex-row md:items-end md:gap-10">
          <div className="flex items-baseline justify-between gap-3 md:block">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--fl-hero-fg-2)] md:text-[13px] md:font-normal md:normal-case md:tracking-normal">
              Outstanding
            </p>
            <p
              className={`whitespace-nowrap font-serif text-[22px] font-bold leading-tight tracking-tight tabular-nums md:mt-1 md:text-[42px] md:font-semibold ${
                lent ? HERO_TONE.income : HERO_TONE.expense
              }`}
            >
              {heroFormat(loan.outstanding)}
            </p>
          </div>
          <dl className="grid grid-cols-3 gap-2 border-t border-[var(--fl-hero-bar-faint)] pt-2 md:flex md:gap-10 md:border-0 md:pt-0 md:pb-1">
            <HeroFigure label={lent ? 'Lent' : 'Borrowed'} value={heroFormat(principal)} />
            <HeroFigure label="Interest so far" value={heroFormat(loan.interestPaid)} />
            {loan.writtenOff > 0 && (
              <HeroFigure label="Written off" value={heroFormat(loan.writtenOff)} />
            )}
          </dl>
        </div>
      </section>

      <section
        aria-label="History"
        className="overflow-hidden rounded-[14px] border border-[var(--fl-border)] bg-[var(--fl-surface)]"
      >
        <h2 className="border-b border-[var(--fl-divider)] px-4 py-3 text-sm font-semibold text-[var(--fl-heading)]">
          History
        </h2>
        {payments.length === 0 ? (
          <p className="px-4 py-3 text-sm text-[var(--fl-text-2)]">No payments yet.</p>
        ) : (
          <ul className="divide-y divide-[var(--fl-divider)]">
            {payments.map((p) => (
              <li key={p.id} className="flex items-center gap-3 px-4 py-3">
                <span className="w-14 shrink-0 text-xs text-[var(--fl-text-2)]">
                  {formatShortDate(p.paidOn)}
                </span>
                <span className="min-w-0 flex-1 text-sm md:truncate text-[var(--fl-text-1)]">
                  {describe(p)}
                </span>
                {!isReadOnly && p.undoable && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      undo.reset()
                      setUndoing(p)
                    }}
                  >
                    Undo
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {paying && <RecordPaymentModal loan={loan} onClose={() => setPaying(false)} />}
      {editing && <EditTermsModal loan={loan} onClose={() => setEditing(false)} />}
      {writingOff && (
        <ConfirmDialog
          title="Write off loan"
          confirmLabel="Write off"
          pendingLabel="Writing off…"
          onConfirm={() => writeOff.mutate()}
          onCancel={() => setWritingOff(false)}
          isPending={writeOff.isPending}
          errorMessage={
            writeOff.isError ? errorDetail(writeOff.error, 'Could not write this off.') : null
          }
        >
          Write off the {format(loan.outstanding)} {person} still owes? It&rsquo;s recorded as a
          one-off loss and the loan closes. You can undo this from the history.
          <div className="mt-4">
            <label
              htmlFor="write-off-date"
              className="mb-1 block text-sm font-medium text-[var(--fl-text-1)]"
            >
              Written off on
            </label>
            <DatePicker id="write-off-date" value={writeOffDate} onChange={setWriteOffDate} />
          </div>
        </ConfirmDialog>
      )}
      {deleting && (
        <ConfirmDialog
          title="Delete loan"
          confirmLabel="Delete"
          pendingLabel="Deleting…"
          onConfirm={() => remove.mutate()}
          onCancel={() => setDeleting(false)}
          isPending={remove.isPending}
          errorMessage={
            remove.isError ? errorDetail(remove.error, 'Could not delete this loan.') : null
          }
        >
          {loan.alreadyOwed
            ? `Delete this loan? It and its ${format(loan.outstanding)} balance are removed. No other account changes.`
            : openingAccountName
              ? lent
                ? `Delete this loan? The ${format(loan.outstanding)} is returned to ${openingAccountName} and the loan is removed.`
                : `Delete this loan? The ${format(loan.outstanding)} is taken back out of ${openingAccountName} and the loan is removed.`
              : 'Delete this loan? The account and its balance stay; only the loan details are removed.'}
        </ConfirmDialog>
      )}
      {undoing && (
        <ConfirmDialog
          title={undoing.kind === 'WRITE_OFF' ? 'Undo write-off' : 'Undo payment'}
          confirmLabel="Undo"
          pendingLabel="Undoing…"
          onConfirm={() => undo.mutate(undoing)}
          onCancel={() => setUndoing(null)}
          isPending={undo.isPending}
          errorMessage={undo.isError ? errorDetail(undo.error, 'Could not undo this.') : null}
        >
          {undoing.kind === 'WRITE_OFF'
            ? 'The loan reopens with its balance restored.'
            : 'Its transactions are removed and the account balances revert.'}
        </ConfirmDialog>
      )}
    </div>
  )
}
